import { useMemo, useState } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { createBrowserRouter, createHashRouter, RouterProvider, useRoutes, type RouterProviderProps } from "react-router-dom";
import { Toaster } from "@/ui";
import { hostMode } from "@/engine/transport";
import { createEngineQueryClient, useEngineNotifications } from "@/engine/hooks";
import { useApplyTheme } from "./theme";
import { routes } from "./routes";
import { useEngineBoot } from "./RequireSession";
import { useNativeNotifications } from "./notifications";

type AppRouter = RouterProviderProps["router"];

export function createAppRouter(): AppRouter {
  return hostMode() === "extension" ? createHashRouter(routes) : createBrowserRouter(routes);
}

function Effects() {
  useApplyTheme();
  useEngineNotifications();
  useEngineBoot();
  useNativeNotifications();
  return null;
}

/** Providers and global effects without a router; tests wrap this around a MemoryRouter. */
export function AppShell({ children }: { children: React.ReactNode }) {
  const [client] = useState(createEngineQueryClient);
  return (
    <QueryClientProvider client={client}>
      <Effects />
      {children}
      <Toaster />
    </QueryClientProvider>
  );
}

/** The route tree as an element, for use inside any router. */
export function AppRoutes() {
  return useRoutes(routes);
}

export function App({ router }: { router?: AppRouter }) {
  const r = useMemo(() => router ?? createAppRouter(), [router]);
  return (
    <AppShell>
      <RouterProvider router={r} />
    </AppShell>
  );
}
